import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FileText, AlertTriangle, CheckCircle, RefreshCw, Download,
  Clock, FileWarning, ChevronDown, ChevronRight, Info, Settings,
  Eye, EyeOff, SlidersHorizontal, Trash2, Upload,
  Copy, Check, Landmark, Scale, FileMinus, X, History,
} from 'lucide-react';
import { formatAccountName } from '../../utils/accounts';
import { money } from '../../lib/money';
import { cn } from '../../lib/cn';
import {
  Button, IconButton, Tag, Card, Money, DateText, Skeleton, EmptyState,
  ConfirmModal, Input, TextArea, BUTTON_BASE, BUTTON_VARIANTS,
} from '../ui';
import type { TagTone } from '../ui';
import {
  useFirmSettings,
  useMissingStatementGaps,
  useGenerateDemandLetter,
  useGenerateRecordsRequests,
  useDemandLetters,
  useDemandLetterSettings,
  useUpdateDemandLetterSettings,
  useDeleteDemandLetter,
  useUploadExampleLetter,
  useUpdateFirmSettings,
  useDemandLetterPreview,
} from '../../hooks/useDemandLetters';
import type { Case, StatementGap, DemandLetterMeta, DemandLetterSettings, TransferDestinationTransaction } from '../../types';
import SourcePageViewer from '../SourcePageViewer';

interface Props {
  caseId: string;
  caseData: Case;
}

interface ViewerState {
  statementId: number;
  pageNumber: number;
  sourceFile: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMonth(iso: string): string {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateLong(iso: string | null): string {
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** All currency strings in this file (screen + clipboard text) go through money(). */
function formatCurrency(n: number): string {
  return money(n);
}

function groupConsecutiveMonths(months: string[]): string[] {
  if (!months.length) return [];
  const dates = [...months].sort();
  const groups: [string, string][] = [];
  let start = dates[0];
  let end = dates[0];
  for (let i = 1; i < dates.length; i++) {
    const prev = new Date(end + 'T12:00:00');
    prev.setMonth(prev.getMonth() + 1);
    const curr = new Date(dates[i] + 'T12:00:00');
    if (prev.getFullYear() === curr.getFullYear() && prev.getMonth() === curr.getMonth()) {
      end = dates[i];
    } else {
      groups.push([start, end]);
      start = dates[i];
      end = dates[i];
    }
  }
  groups.push([start, end]);
  return groups.map(([s, e]) => {
    const sd = new Date(s + 'T12:00:00');
    const ed = new Date(e + 'T12:00:00');
    if (s === e) return formatMonth(s);
    if (sd.getFullYear() === ed.getFullYear()) {
      return `${sd.toLocaleDateString('en-US', { month: 'long' })}–${ed.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    }
    return `${formatMonth(s)} – ${formatMonth(e)}`;
  });
}

function extractAxiosDetail(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'response' in err &&
    err.response !== null &&
    typeof err.response === 'object' &&
    'data' in err.response &&
    err.response.data !== null &&
    typeof err.response.data === 'object' &&
    'detail' in err.response.data &&
    typeof (err.response.data as Record<string, unknown>).detail === 'string'
  ) {
    return (err.response.data as Record<string, string>).detail;
  }
  return 'Letter generation failed. Please try again.';
}

function itemKey(gap: StatementGap): string {
  return `${gap.gap_type}::${gap.account_label}`;
}

// ---------------------------------------------------------------------------
// Copy button
// ---------------------------------------------------------------------------

function CopyButton({ text, label = 'Copy for Discovery' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={copied ? Check : Copy}
      onClick={handleClick}
      className={copied ? 'text-green-700' : undefined}
    >
      {copied ? 'Copied' : label}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Confidence badge
// ---------------------------------------------------------------------------

function ConfidenceBadge({ score }: { score: number | null }) {
  if (score === null || score === undefined) return null;
  const tone: TagTone = score >= 80 ? 'good' : score >= 50 ? 'neutral' : 'danger';
  return (
    <Tag tone={tone} size="sm">
      {score}%
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// Statement Gap card (amber expandable, checkbox in header)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Remedy routing: who must supply the missing document (server-computed).
// The demand letter only ever contains 'opposing' items — the server enforces
// this; the UI makes the routing visible so nothing looks silently dropped.
// ---------------------------------------------------------------------------

const ROUTING_META: Record<string, { label: string; tone: TagTone; title: string }> = {
  opposing: {
    label: 'Demand from opposing counsel',
    tone: 'opposing',
    title: 'Only the opposing party can produce these documents — goes in the demand letter',
  },
  client: {
    label: 'Ask your client',
    tone: 'client',
    title: "The client's own or joint account — a phone call, not a discovery demand",
  },
  subpoena: {
    label: 'Subpoena candidate',
    tone: 'third',
    title: 'A third party (business/institution) holds these records — subpoena the institution',
  },
  resolve_ownership_first: {
    label: 'Confirm owner first',
    tone: 'unknown',
    title: "This account's owner is unconfirmed — set it on the Accounts page before routing",
  },
};

function RoutingBadge({ rf }: { rf?: string }) {
  const meta = ROUTING_META[rf || 'resolve_ownership_first'];
  if (!meta) return null;
  return (
    <Tag tone={meta.tone} title={meta.title}>
      {meta.label}
    </Tag>
  );
}

/** Shared card shell for the finding cards — Card recipe with the clay accent. */
const FINDING_CARD = 'rounded-lg border border-clay-200 bg-surface-card overflow-hidden';
const FINDING_HEADER = 'w-full flex items-center gap-3 px-4 py-3 bg-clay-50 hover:bg-clay-100 transition-colors cursor-pointer select-none';
const FINDING_CHECKBOX = 'w-4 h-4 rounded border-clay-300 text-green-700 accent-green-700 cursor-pointer';

interface GapCardProps {
  gap: StatementGap;
  checked: boolean;
  onToggle: (key: string) => void;
  highlight?: boolean;
}

function GapCard({ gap, checked, onToggle, highlight }: GapCardProps) {
  const [expanded, setExpanded] = useState(false);
  const ranges = groupConsecutiveMonths(gap.missing_months);
  const accountSuffix = gap.last4 ? `…${gap.last4}` : '';
  const institution = gap.institution || 'Unknown Institution';
  const key = itemKey(gap);

  return (
    <div
      data-gap-last4={gap.last4 || ''}
      className={cn(
        FINDING_CARD,
        highlight && 'border-danger-accent ring-2 ring-danger-accent ring-offset-1',
      )}
    >
      <div
        className={cn(FINDING_HEADER, checked ? '' : 'opacity-50')}
        onClick={() => setExpanded(e => !e)}
      >
        <div
          onClick={e => e.stopPropagation()}
          className="shrink-0"
        >
          {/* eslint-disable-next-line no-restricted-syntax -- native checkbox in a selectable finding card: no ui Checkbox primitive exists yet */}
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(key)}
            className={FINDING_CHECKBOX}
          />
        </div>

        <div className="shrink-0 text-clay-500">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FileWarning size={16} className="text-clay-600 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-clay-900 text-sm leading-tight">
              {institution} {gap.account_type}
              {accountSuffix && <span className="text-clay-500 font-normal"> {accountSuffix}</span>}
            </p>
            <p className="text-xs text-clay-600 mt-1">
              {gap.missing_months.length} missing month{gap.missing_months.length !== 1 ? 's' : ''}
              {gap.coverage_start && gap.coverage_end && (
                <> &bull; Coverage {formatDate(gap.coverage_start)} – {formatDate(gap.coverage_end)}</>
              )}
            </p>
          </div>
        </div>

        <RoutingBadge rf={gap.request_from} />
        <Tag tone="neutral">Statement gap</Tag>
      </div>

      {expanded && ranges.length > 0 && (
        <div className="border-t border-clay-200 bg-cream-light px-5 pb-4 pt-3">
          <div className="text-xs font-medium text-clay-700 mb-2">Missing periods:</div>
          <div className="flex flex-wrap gap-2">
            {ranges.map(r => (
              <Tag key={r} tone="neutral">{r}</Tag>
            ))}
          </div>
          {gap.notes && (
            <div className="mt-2 flex items-start gap-2 text-xs text-clay-600">
              <Info size={12} className="mt-px shrink-0" />
              <span>{gap.notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Balance-discontinuity card — unproduced periods proven by the account's own
// printed balances (invisible to month-based gap detection).
// ---------------------------------------------------------------------------

interface DiscontinuityCardProps {
  gap: StatementGap;
  checked: boolean;
  onToggle: (key: string) => void;
}

function DiscontinuityCard({ gap, checked, onToggle }: DiscontinuityCardProps) {
  const [expanded, setExpanded] = useState(false);
  const accountSuffix = gap.last4 ? `…${gap.last4}` : '';
  const institution = gap.institution || 'Unknown Institution';
  const key = itemKey(gap);
  const isBreak = gap.discontinuity_kind === 'continuity_break';
  const delta = gap.balance_delta;

  return (
    <div className={FINDING_CARD}>
      <div
        className={cn(FINDING_HEADER, checked ? '' : 'opacity-50')}
        onClick={() => setExpanded(e => !e)}
      >
        <div onClick={e => e.stopPropagation()} className="shrink-0">
          {/* eslint-disable-next-line no-restricted-syntax -- native checkbox in a selectable finding card: no ui Checkbox primitive exists yet */}
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(key)}
            className={FINDING_CHECKBOX}
          />
        </div>

        <div className="shrink-0 text-clay-500">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Scale size={16} className="text-clay-600 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-clay-900 text-sm leading-tight">
              {institution} {gap.account_type}
              {accountSuffix && <span className="text-clay-500 font-normal"> {accountSuffix}</span>}
            </p>
            <p className="text-xs text-clay-600 mt-1">
              {isBreak ? (
                <>Statements don't chain at {gap.coverage_start ? formatDate(gap.coverage_start) : '?'} / {gap.coverage_end ? formatDate(gap.coverage_end) : '?'}</>
              ) : (
                <>Unproduced {gap.coverage_start ? formatDate(gap.coverage_start) : '?'} – {gap.coverage_end ? formatDate(gap.coverage_end) : '?'}</>
              )}
              {delta != null && Math.abs(delta) > 0.01 && (
                <> &bull; <span className="font-medium text-clay-800">{formatCurrency(Math.abs(delta))} moved</span></>
              )}
            </p>
          </div>
        </div>

        <RoutingBadge rf={gap.request_from} />
        <Tag tone="neutral">{isBreak ? 'Broken chain' : 'Balance-proven gap'}</Tag>
      </div>

      {expanded && (
        <div className="border-t border-clay-200 bg-cream-light px-5 pb-4 pt-3">
          {gap.notes && (
            <div className="flex items-start gap-2 text-xs text-clay-700">
              <Info size={12} className="mt-px shrink-0" />
              <span>{gap.notes}</span>
            </div>
          )}
          {gap.source_files && gap.source_files.length === 2 && (
            <p className="mt-2 text-xs text-clay-600">
              Between <span className="font-medium">{gap.source_files[0]}</span> and{' '}
              <span className="font-medium">{gap.source_files[1]}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Incomplete-copy card — the produced statement's own pagination shows pages
// were omitted from the production.
// ---------------------------------------------------------------------------

function IncompleteCopyCard({ gap, checked, onToggle }: {
  gap: StatementGap; checked: boolean; onToggle: (key: string) => void;
}) {
  const accountSuffix = gap.last4 ? `…${gap.last4}` : '';
  const institution = gap.institution || 'Unknown Institution';
  const key = itemKey(gap);
  const delta = gap.balance_delta;
  return (
    <div className={FINDING_CARD}>
      <div className={cn('w-full flex items-center gap-3 px-4 py-3 bg-clay-50', checked ? '' : 'opacity-50')}>
        {/* eslint-disable-next-line no-restricted-syntax -- native checkbox in a selectable finding card: no ui Checkbox primitive exists yet */}
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(key)}
          className={cn(FINDING_CHECKBOX, 'shrink-0')}
        />
        <FileMinus size={16} className="text-clay-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-clay-900 text-sm leading-tight">
            {institution} {gap.account_type}
            {accountSuffix && <span className="text-clay-500 font-normal"> {accountSuffix}</span>}
          </p>
          <p className="text-xs text-clay-600 mt-1">
            {gap.coverage_start ? formatDate(gap.coverage_start) : '?'} – {gap.coverage_end ? formatDate(gap.coverage_end) : '?'}
            {' '}&bull; {gap.pages_produced} of {gap.pages_declared} pages produced
            {delta != null && Math.abs(delta) > 0.01 && (
              <> &bull; <span className="font-medium text-clay-800">{formatCurrency(Math.abs(delta))} on missing pages</span></>
            )}
          </p>
        </div>
        <RoutingBadge rf={gap.request_from} />
        <Tag tone="neutral">Incomplete copy</Tag>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transfer destination transaction row
// ---------------------------------------------------------------------------

interface TransferTxnRowProps {
  txn: TransferDestinationTransaction;
  onViewSource: (state: ViewerState) => void;
}

function TransferTxnRow({ txn, onViewSource }: TransferTxnRowProps) {
  const hasSource = !!(txn.statement_id && txn.source_page);
  const fullCitation = txn.source_file && txn.source_page
    ? `${txn.source_file} (p.${txn.source_page})`
    : '';
  const shortCitation = fullCitation.length > 26
    ? `${fullCitation.substring(0, 23)}…`
    : fullCitation;

  return (
    <tr className="h-11 hover:bg-cream transition-colors">
      <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap align-top">
        <DateText value={txn.date} />
      </td>
      <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap align-top">
        {txn.source_bank_label ? formatAccountName(txn.source_bank_label) : '—'}
      </td>
      <td className="px-3 py-2 text-xs text-ink-soft align-top">
        <span>{txn.description || '—'}</span>
        {txn.raw_text && txn.raw_text !== txn.description && (
          <p className="text-ink-faint font-mono text-xs mt-1 truncate max-w-xs" title={txn.raw_text}>
            {txn.raw_text.substring(0, 80)}{txn.raw_text.length > 80 ? '…' : ''}
          </p>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <ConfidenceBadge score={txn.confidence_score} />
      </td>
      <td className="px-3 py-2 align-top">
        {hasSource ? (
          <Button
            variant="ghost"
            size="sm"
            icon={Eye}
            title={fullCitation}
            onClick={() => onViewSource({
              statementId: txn.statement_id!,
              pageNumber: txn.source_page!,
              sourceFile: txn.source_file,
            })}
          >
            {shortCitation}
          </Button>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap align-top">
        <Money value={Math.abs(txn.amount)} className="text-xs font-semibold text-danger" />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Transfer destination card (amber expandable with transaction table)
// ---------------------------------------------------------------------------

interface TransferCardProps {
  gap: StatementGap;
  checked: boolean;
  onToggle: (key: string) => void;
  onViewSource: (state: ViewerState) => void;
}

function buildTxnLine(t: TransferDestinationTransaction): string {
  const dateStr = formatDateLong(t.date).padEnd(14);
  const src = (t.source_bank_label || '').substring(0, 22).padEnd(24);
  const desc = (t.description || '').substring(0, 34).padEnd(36);
  const amt = formatCurrency(Math.abs(t.amount)).padStart(12);
  const citation = t.source_file && t.source_page
    ? ` [${t.source_file}, p.${t.source_page}]`
    : '';
  return `${dateStr}${src}${desc}${amt}${citation}`;
}

function buildTransferCardText(gap: StatementGap): string {
  const institution = gap.institution || gap.account_label || 'Unknown';
  const accountSuffix = gap.last4 ? ` …${gap.last4}` : '';
  const period =
    gap.first_transfer_date && gap.last_transfer_date && gap.first_transfer_date !== gap.last_transfer_date
      ? `${formatDateShort(gap.first_transfer_date)} – ${formatDateShort(gap.last_transfer_date)}`
      : formatDateShort(gap.first_transfer_date);
  const transactions = gap.transactions ?? [];

  const lines = [
    `Account: ${institution}${accountSuffix}`,
    `Total Transferred: ${formatCurrency(Math.abs(gap.total_transferred ?? 0))}`,
    `Number of Transfers: ${gap.transfer_count ?? 0}`,
    `Period: ${period}`,
    '',
    'Transaction Detail:',
    '-'.repeat(86),
    `${'Date'.padEnd(14)}${'Source Account'.padEnd(24)}${'Description'.padEnd(36)}${'Amount'.padStart(12)}  Citation`,
    '-'.repeat(86),
    ...transactions.map(buildTxnLine),
    '-'.repeat(86),
  ];
  return lines.join('\n');
}

function TransferCard({ gap, checked, onToggle, onViewSource }: TransferCardProps) {
  const [expanded, setExpanded] = useState(false);
  const key = itemKey(gap);
  const institution = gap.institution || gap.account_label || 'Unknown';
  const accountSuffix = gap.last4 ? `…${gap.last4}` : '';
  const transactions = gap.transactions ?? [];

  const period =
    gap.first_transfer_date && gap.last_transfer_date && gap.first_transfer_date !== gap.last_transfer_date
      ? `${formatDateShort(gap.first_transfer_date)} – ${formatDateShort(gap.last_transfer_date)}`
      : formatDateShort(gap.first_transfer_date);

  return (
    <div className={FINDING_CARD}>
      <div
        className={cn(FINDING_HEADER, checked ? '' : 'opacity-50')}
        onClick={() => setExpanded(e => !e)}
      >
        <div
          onClick={e => e.stopPropagation()}
          className="shrink-0"
        >
          {/* eslint-disable-next-line no-restricted-syntax -- native checkbox in a selectable finding card: no ui Checkbox primitive exists yet */}
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(key)}
            className={FINDING_CHECKBOX}
          />
        </div>

        <div className="shrink-0 text-clay-500">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Landmark size={16} className="text-clay-600 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-clay-900 text-sm leading-tight">
              {institution}
              {accountSuffix && <span className="text-clay-500 font-normal"> {accountSuffix}</span>}
            </p>
            <p className="text-xs text-clay-600 mt-1">{gap.account_type}</p>
          </div>
        </div>

        <div className="hidden sm:block text-right shrink-0 w-36">
          <p className="text-xs text-clay-600">{period}</p>
          <p className="text-xs text-clay-400">period</p>
        </div>

        <div className="text-right shrink-0">
          <p className="text-sm">
            <Money value={Math.abs(gap.total_transferred ?? 0)} className="font-bold text-danger" />
          </p>
          <p className="text-xs text-clay-600">
            {gap.transfer_count} transfer{gap.transfer_count !== 1 ? 's' : ''}
          </p>
        </div>

        <RoutingBadge rf={gap.request_from} />
        <Tag tone="danger">No statements</Tag>

        <div onClick={e => e.stopPropagation()} className="shrink-0">
          <CopyButton text={buildTransferCardText(gap)} />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-clay-200 bg-cream-light">
          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              {/* eslint-disable-next-line no-restricted-syntax -- citation buttons + confidence tags per cell: restyled in place, DataTable cannot host these */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="t-eyebrow text-left px-3 py-2 w-28">Date</th>
                    <th className="t-eyebrow text-left px-3 py-2 w-36">Source Account</th>
                    <th className="t-eyebrow text-left px-3 py-2">Description</th>
                    <th className="t-eyebrow text-left px-3 py-2 w-20">Confidence</th>
                    <th className="t-eyebrow text-left px-3 py-2 w-40">Citation</th>
                    <th className="t-eyebrow text-right px-3 py-2 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {transactions.map((txn, i) => (
                    <TransferTxnRow key={i} txn={txn} onViewSource={onViewSource} />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border">
                    <td colSpan={5} className="px-3 py-2 text-xs font-semibold text-ink-soft text-right">Total:</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Money value={Math.abs(gap.total_transferred ?? 0)} className="text-xs font-bold text-danger" />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="px-5 py-3 text-xs text-clay-600 italic">
              No individual transaction detail available.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy text builders for "Copy All" in each section
// ---------------------------------------------------------------------------

function buildAllTransfersText(items: StatementGap[]): string {
  const totalAmount = items.reduce((s, g) => s + (g.total_transferred ?? 0), 0);
  const totalCount = items.reduce((s, g) => s + (g.transfer_count ?? 0), 0);
  const sections = [
    'Transfers to Accounts With No Statements on File',
    '='.repeat(86),
    `Total Accounts: ${items.length}`,
    `Total Transferred: ${formatCurrency(Math.abs(totalAmount))}`,
    `Total Transfers: ${totalCount}`,
    '',
    ...items.flatMap((gap, i) => [
      `${i + 1}. ${buildTransferCardText(gap)}`,
      '',
    ]),
    'Please produce all account statements for the above accounts.',
  ];
  return sections.join('\n');
}

// ---------------------------------------------------------------------------
// Trailing gap card (amber expandable, checkbox in header)
// ---------------------------------------------------------------------------

interface TrailingGapCardProps {
  gap: StatementGap;
  checked: boolean;
  onToggle: (key: string) => void;
  caseId: string;
}

function TrailingGapCard({ gap, checked, onToggle, caseId }: TrailingGapCardProps) {
  const [expanded, setExpanded] = useState(false);
  const trailingMonths = gap.trailing_months ?? [];
  const ranges = groupConsecutiveMonths(trailingMonths);
  const monthsStale = gap.months_stale ?? trailingMonths.length;
  const accountSuffix = gap.last4 ? `…${gap.last4}` : '';
  const institution = gap.institution || 'Unknown Institution';
  const lastCovered = gap.coverage_end ? formatDateShort(gap.coverage_end) : '—';
  const key = itemKey(gap);

  return (
    <div className={FINDING_CARD}>
      <div
        className={cn(FINDING_HEADER, checked ? '' : 'opacity-50')}
        onClick={() => setExpanded(e => !e)}
      >
        <div onClick={e => e.stopPropagation()} className="shrink-0">
          {/* eslint-disable-next-line no-restricted-syntax -- native checkbox in a selectable finding card: no ui Checkbox primitive exists yet */}
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(key)}
            className={FINDING_CHECKBOX}
          />
        </div>

        <div className="shrink-0 text-clay-500">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Clock size={16} className="text-clay-600 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-clay-900 text-sm leading-tight">
              {institution} {gap.account_type}
              {accountSuffix && <span className="text-clay-500 font-normal"> {accountSuffix}</span>}
            </p>
            <p className="text-xs text-clay-600 mt-1">
              Last statement through {lastCovered}
              {monthsStale > 0 && (
                <> &bull; <span className="font-medium">{monthsStale} month{monthsStale !== 1 ? 's' : ''} not produced</span></>
              )}
            </p>
          </div>
        </div>

        <RoutingBadge rf={gap.request_from} />
        <Tag tone="warn">Not current</Tag>
      </div>

      {expanded && ranges.length > 0 && (
        <div className="border-t border-clay-200 bg-cream-light px-5 pb-4 pt-3">
          <div className="text-xs font-medium text-clay-700 mb-2">Months not produced:</div>
          <div className="flex flex-wrap gap-2">
            {ranges.map(r => (
              <Tag key={r} tone="warn">{r}</Tag>
            ))}
          </div>
          {gap.notes && (
            <div className="mt-2 flex items-start gap-2 text-xs text-clay-600">
              <Info size={12} className="mt-px shrink-0" />
              <span>{gap.notes}</span>
            </div>
          )}
          <div className="mt-2">
            <Link
              to={`/cases/${caseId}/discovery/tracker`}
              className="text-xs font-medium text-clay-700 hover:text-clay-900 underline underline-offset-2"
            >
              View in tracker
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leading gap card (amber expandable, checkbox in header) — the mirror of the
// trailing card: months between the discovery anchor and the earliest
// statement produced.
// ---------------------------------------------------------------------------

interface LeadingGapCardProps {
  gap: StatementGap;
  checked: boolean;
  onToggle: (key: string) => void;
  caseId: string;
}

function LeadingGapCard({ gap, checked, onToggle, caseId }: LeadingGapCardProps) {
  const [expanded, setExpanded] = useState(false);
  const leadingMonths = gap.leading_months ?? [];
  const ranges = groupConsecutiveMonths(leadingMonths);
  const monthsMissing = leadingMonths.length;
  const accountSuffix = gap.last4 ? `…${gap.last4}` : '';
  const institution = gap.institution || 'Unknown Institution';
  const firstProduced = gap.coverage_end ? formatDateShort(gap.coverage_end) : '—';
  const key = itemKey(gap);

  return (
    <div className={FINDING_CARD}>
      <div
        className={cn(FINDING_HEADER, checked ? '' : 'opacity-50')}
        onClick={() => setExpanded(e => !e)}
      >
        <div onClick={e => e.stopPropagation()} className="shrink-0">
          {/* eslint-disable-next-line no-restricted-syntax -- native checkbox in a selectable finding card: no ui Checkbox primitive exists yet */}
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(key)}
            className={FINDING_CHECKBOX}
          />
        </div>

        <div className="shrink-0 text-clay-500">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <History size={16} className="text-clay-600 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-clay-900 text-sm leading-tight">
              {institution} {gap.account_type}
              {accountSuffix && <span className="text-clay-500 font-normal"> {accountSuffix}</span>}
            </p>
            <p className="text-xs text-clay-600 mt-1">
              Earliest statement begins {firstProduced}
              {monthsMissing > 0 && (
                <> &bull; <span className="font-medium">{monthsMissing} earlier month{monthsMissing !== 1 ? 's' : ''} not produced</span></>
              )}
            </p>
          </div>
        </div>

        <RoutingBadge rf={gap.request_from} />
        <Tag tone="warn">Starts late</Tag>
      </div>

      {expanded && ranges.length > 0 && (
        <div className="border-t border-clay-200 bg-cream-light px-5 pb-4 pt-3">
          <div className="text-xs font-medium text-clay-700 mb-2">Months not produced:</div>
          <div className="flex flex-wrap gap-2">
            {ranges.map(r => (
              <Tag key={r} tone="warn">{r}</Tag>
            ))}
          </div>
          {gap.notes && (
            <div className="mt-2 flex items-start gap-2 text-xs text-clay-600">
              <Info size={12} className="mt-px shrink-0" />
              <span>{gap.notes}</span>
            </div>
          )}
          <div className="mt-2">
            <Link
              to={`/cases/${caseId}/discovery/tracker`}
              className="text-xs font-medium text-clay-700 hover:text-clay-900 underline underline-offset-2"
            >
              View in tracker
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live letter preview
// ---------------------------------------------------------------------------

function buildPreviewText(
  gaps: StatementGap[],
  checkedKeys: Set<string>,
  firmSettings: { firm_name?: string | null; attorney_name?: string | null } | undefined,
  caseData: Case,
  settings: DemandLetterSettings,
): string {
  const selected = gaps.filter(g => checkedKeys.has(itemKey(g)));
  if (!selected.length) return '(No items selected.)';

  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const firmName = firmSettings?.firm_name || '[Firm Name]';
  const attorney = firmSettings?.attorney_name || '[Attorney Name]';

  const counselName = caseData.opposing_counsel_name;
  const counselFirm = caseData.opposing_counsel_firm;
  const counselAddress = caseData.opposing_counsel_address;
  const counselEmail = caseData.opposing_counsel_email;
  const opposing = caseData.opposing_party_name;

  const recipientLines: string[] = [];
  if (counselName) {
    recipientLines.push(counselName);
    if (counselFirm) recipientLines.push(counselFirm);
    // Mirror the generated docx, which renders the mailing address line-by-line.
    if (counselAddress) recipientLines.push(...counselAddress.split('\n').map(l => l.trim()).filter(Boolean));
    if (counselEmail) recipientLines.push(counselEmail);
  } else if (opposing) {
    recipientLines.push(opposing);
    recipientLines.push('Opposing Party / Counsel of Record');
  } else {
    recipientLines.push('[Opposing Counsel Name]');
  }

  const reParts: string[] = [];
  if (caseData.client_name && opposing) {
    reParts.push(`In the Matter of the Marriage of ${caseData.client_name} and ${opposing}`);
  } else {
    reParts.push(caseData.name || '[Case Name]');
  }
  if (caseData.cause_number) reParts.push(`Cause No. ${caseData.cause_number}`);
  if (caseData.court) reParts.push(caseData.court);
  reParts.push(caseData.county ? `${caseData.county} County, Texas` : 'Texas');

  const delivery = settings.delivery_method || 'Via Electronic Mail / Certified Mail';
  const noticeDays = settings.notice_days || 10;

  const describeAccount = (g: StatementGap) => {
    const inst = g.institution || 'Unknown Institution';
    const suffix = g.last4 ? ` (…${g.last4})` : '';
    return `${inst}${suffix}`;
  };

  const gapItems = selected.filter(g => g.gap_type === 'missing_months');
  const leadingItems = selected.filter(g => g.gap_type === 'leading_gap');
  const transferItems = selected.filter(g => g.gap_type === 'transfer_destination');
  const trailingItems = selected.filter(g => g.gap_type === 'trailing_gap');

  const intro = settings.intro_override
    ? settings.intro_override
    : (() => {
        const hasGaps = gapItems.length > 0;
        const hasLeading = leadingItems.length > 0;
        const hasTransfers = transferItems.length > 0;
        const hasTrailing = trailingItems.length > 0;
        const parts: string[] = [];
        if (hasGaps) parts.push('missing monthly statements within the date ranges already produced');
        if (hasLeading) parts.push('accounts whose earliest statement produced begins after the date from which discovery was requested');
        if (hasTransfers) parts.push('accounts that received transferred funds but for which no statements have been produced');
        if (hasTrailing) parts.push('accounts for which statements have not been produced through the present date');
        const bodyDesc = parts.length > 0
          ? parts.join('; and ')
          : 'missing financial account statements';
        return `Dear Counsel:\n\nPlease be advised that our review of the financial documents produced to date has identified ${bodyDesc}. We request that you produce the items listed below at your earliest opportunity and no later than ${noticeDays} day${noticeDays !== 1 ? 's' : ''} from the date of this letter.\n\nIf you believe any of the following statements do not exist or are not within your client's possession, custody, or control, please advise in writing.`;
      })();

  const itemLines: string[] = [];
  let n = 1;
  let sectionNum = 0;
  const roman = (i: number) => ['I', 'II', 'III', 'IV', 'V'][i - 1] ?? String(i);

  if (gapItems.length > 0) {
    sectionNum++;
    itemLines.push(`${roman(sectionNum)}. MISSING STATEMENT GAPS`);
    for (const g of gapItems) {
      const ranges = groupConsecutiveMonths(g.missing_months);
      itemLines.push(`${n}. Please produce all statements for ${describeAccount(g)} for the following period(s): ${ranges.join('; ')}.`);
      n++;
    }
    itemLines.push('');
  }
  if (leadingItems.length > 0) {
    sectionNum++;
    itemLines.push(`${roman(sectionNum)}. STATEMENTS NOT PRODUCED BACK TO THE DISCOVERY DATE`);
    for (const g of leadingItems) {
      const ranges = groupConsecutiveMonths(g.leading_months ?? []);
      const anchor = g.anchor ? formatDate(g.anchor) : null;
      const lead = anchor
        ? `No statements have been produced for ${describeAccount(g)} for the period beginning ${anchor}, the date from which discovery was requested.`
        : `No statements have been produced for ${describeAccount(g)} for the period preceding the earliest statement produced.`;
      itemLines.push(`${n}. ${lead} Please produce all statements for the following period(s): ${ranges.join('; ')}.`);
      n++;
    }
    itemLines.push('');
  }
  if (transferItems.length > 0) {
    sectionNum++;
    itemLines.push(`${roman(sectionNum)}. ACCOUNTS RECEIVING TRANSFERRED FUNDS`);
    for (const g of transferItems) {
      const total = g.total_transferred ? formatCurrency(g.total_transferred) : 'funds';
      const count = g.transfer_count && g.transfer_count > 1 ? ` in ${g.transfer_count} transactions` : '';
      itemLines.push(`${n}. Our client's records reflect that ${total}${count} was transferred to ${describeAccount(g)}. Please produce all statements for this account for the entire period during which these transfers occurred.`);
      n++;
    }
    itemLines.push('');
  }
  if (trailingItems.length > 0) {
    sectionNum++;
    itemLines.push(`${roman(sectionNum)}. ACCOUNTS NOT PRODUCED THROUGH PRESENT DATE`);
    for (const g of trailingItems) {
      const months = g.trailing_months ?? [];
      const firstMonth = months.length > 0 ? formatMonth(months[0]) : 'the last statement date';
      itemLines.push(`${n}. Please produce all statements for ${describeAccount(g)} from ${firstMonth} through the present.`);
      n++;
    }
  }

  return [
    firmName,
    attorney,
    '',
    today,
    '',
    delivery,
    '',
    ...recipientLines,
    '',
    `RE: ${reParts.join('; ')}`,
    '',
    '─'.repeat(60),
    '',
    intro,
    '',
    'STATEMENTS REQUESTED:',
    '',
    ...itemLines,
    '',
    'Respectfully submitted,',
    '',
    attorney,
    firmName,
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Letter history row
// ---------------------------------------------------------------------------

interface LetterRowProps {
  letter: DemandLetterMeta;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}

function LetterPreviewModal({ letterId, onClose }: { letterId: number; onClose: () => void }) {
  const { data, isLoading, error } = useDemandLetterPreview(letterId);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-(--z-modal) grid place-items-center overflow-y-auto p-4 print:static print:block print:overflow-visible print:p-0"
      onClick={onClose}
    >
      {/* Scrim is a sibling (not a wrapper) so print can drop it and keep the letter. */}
      <div aria-hidden="true" data-print="hide" className="absolute inset-0 bg-ink/40 animate-scrim" />
      <div
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-border bg-surface-card shadow-modal animate-dialog print:max-h-none print:rounded-none print:border-0 print:bg-transparent print:shadow-none"
        onClick={e => e.stopPropagation()}
      >
        <div data-print="hide" className="flex shrink-0 items-center justify-between gap-4 border-b border-border-subtle px-5 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-green-700 shrink-0" />
            <h3 className="text-sm font-semibold text-ink truncate">
              {data?.title || 'Letter Preview'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              icon={Download}
              onClick={() => window.open(`/api/demand-letters/${letterId}/download`, '_blank')}
            >
              Download .docx
            </Button>
            <IconButton icon={X} aria-label="Close" variant="ghost" size="md" onClick={onClose} />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-surface-well p-6 print:overflow-visible print:bg-transparent print:p-0">
          {isLoading && (
            <div data-print="hide" className="space-y-3 rounded-lg border border-border bg-surface-raised p-6" role="status" aria-label="Loading preview">
              <Skeleton width="55%" />
              <Skeleton />
              <Skeleton />
              <Skeleton width="80%" />
            </div>
          )}
          {error && (
            <div data-print="hide" className="text-sm text-danger py-12 text-center">
              Couldn't load preview. Try downloading instead.
            </div>
          )}
          {data?.html && (
            /* The letter renders as a white sheet — the document metaphor. */
            <div className="rounded-lg border border-border bg-surface-raised p-6 print:rounded-none print:border-0 print:p-0">
              <article
                className="letter-preview"
                dangerouslySetInnerHTML={{ __html: data.html }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LetterRow({ letter, onDelete, isDeleting, onPreview }: LetterRowProps & { onPreview: (id: number) => void }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-cream transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <FileText size={16} className="text-green-700 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink truncate">{letter.title || 'Demand Letter'}</div>
          {letter.generated_at && (
            <div className="text-xs text-ink-faint flex items-center gap-1 mt-1">
              <Clock size={12} />
              {new Date(letter.generated_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">
        <Button variant="secondary" size="sm" icon={Eye} onClick={() => onPreview(letter.id)}>
          Preview
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={Download}
          onClick={() => window.open(`/api/demand-letters/${letter.id}/download`, '_blank')}
        >
          Download
        </Button>
        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <Button
              variant="danger"
              size="sm"
              disabled={isDeleting}
              onClick={() => { onDelete(letter.id); setConfirmDelete(false); }}
            >
              {isDeleting ? '…' : 'Confirm'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <IconButton
            icon={Trash2}
            aria-label="Delete letter"
            variant="danger-ghost"
            size="sm"
            onClick={() => setConfirmDelete(true)}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Issue group header (consistent across all three finding types)
// ---------------------------------------------------------------------------

function GroupHeader({
  icon,
  title,
  subtitle,
  count,
  showSelectAll,
  allChecked,
  onToggleAll,
  extra,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  count: number;
  showSelectAll: boolean;
  allChecked: boolean;
  onToggleAll: () => void;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 px-1">
      <span className="shrink-0 text-clay-600">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          <Tag shape="pill" tone="neutral">{count}</Tag>
        </div>
        {subtitle && <p className="text-xs text-ink-faint mt-1 truncate">{subtitle}</p>}
      </div>
      {extra}
      {showSelectAll && (
        <Button variant="secondary" size="sm" onClick={onToggleAll}>
          {allChecked ? 'Deselect all' : 'Select all'}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export default function DemandLetterTab({ caseId, caseData }: Props) {
  const { data: firmSettings } = useFirmSettings();
  const { data: gapData, isLoading: gapsLoading, refetch: refetchGaps, isFetching } = useMissingStatementGaps(caseId);
  const { data: lettersData } = useDemandLetters(caseId);
  const { data: savedSettings } = useDemandLetterSettings(caseId);
  const generateMutation = useGenerateDemandLetter(caseId);
  const recordsRequestMutation = useGenerateRecordsRequests(caseId);
  const updateSettings = useUpdateDemandLetterSettings(caseId);
  const deleteMutation = useDeleteDemandLetter(caseId);
  const uploadExampleMutation = useUploadExampleLetter();
  const updateFirmMutation = useUpdateFirmSettings();
  const exampleInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  // Money Trail (and other tabs) deep-link here with ?focus_last4=1633,9720 to
  // pre-select + scroll to the gap(s) the lawyer chose to act on.
  const focusParam = searchParams.get('focus_last4') || '';
  const focusLast4 = new Set(focusParam.split(',').map((s) => s.trim()).filter(Boolean));
  // Chain-aware focus: a chain's merged gap reports under the TERMINAL
  // member's last4, but the tracker link (and its deep link) may name an
  // earlier member — match on the gap's own last4 OR any chain member's.
  const gapMatchesFocus = (g: StatementGap) => {
    if (g.last4 && focusLast4.has(g.last4)) return true;
    return (g.chain_labels ?? []).some((label) => {
      const m = label.match(/(\d{4})(?!.*\d)/);
      return !!m && focusLast4.has(m[1]);
    });
  };

  const handleExampleLetterUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadExampleMutation.mutateAsync(file);
    } catch (err) {
      console.error(err);
    }
    if (exampleInputRef.current) exampleInputRef.current.value = '';
  };

  const [confirmClearExample, setConfirmClearExample] = useState(false);

  const handleClearExampleLetter = async () => {
    setConfirmClearExample(false);
    try {
      await updateFirmMutation.mutateAsync({ clear_example_letter: true });
    } catch (err) {
      console.error(err);
    }
  };

  const allGaps = gapData?.gaps ?? [];
  const letters = lettersData?.letters ?? [];
  const missingItems = allGaps.filter(g => g.gap_type === 'missing_months');
  const leadingItems = allGaps.filter(g => g.gap_type === 'leading_gap');
  const transferItems = allGaps.filter(g => g.gap_type === 'transfer_destination');
  const trailingItems = allGaps.filter(g => g.gap_type === 'trailing_gap');
  const discontinuityItems = allGaps.filter(g => g.gap_type === 'balance_discontinuity');
  const incompleteItems = allGaps.filter(g => g.gap_type === 'incomplete_copy');

  // Source page viewer state
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const [previewLetterId, setPreviewLetterId] = useState<number | null>(null);

  // Checkbox state — all checked by default; reconcile on each gap list change
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [knownKeys, setKnownKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const incomingKeys = new Set(allGaps.map(itemKey));
    const newKeys = allGaps.filter(g => !knownKeys.has(itemKey(g)));
    if (newKeys.length > 0) {
      setCheckedKeys(prev => {
        const next = new Set(prev);
        // Default-check only items the demand letter can lawfully contain —
        // documents the OPPOSING party must supply. Client/joint items go on
        // the client checklist and third-party items on the subpoena list;
        // they are shown (with routing badges) but not letter-selected.
        newKeys.forEach(g => {
          if (g.request_from === 'opposing') {
            next.add(itemKey(g));
          }
        });
        return next;
      });
      setKnownKeys(incomingKeys);
    }
  }, [allGaps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Honor a ?focus_last4 deep-link: check the flagged account's gap(s) and scroll
  // the first into view so the lawyer lands exactly on what they came to demand.
  useEffect(() => {
    if (!focusParam || allGaps.length === 0) return;
    const matches = allGaps.filter(gapMatchesFocus);
    if (matches.length === 0) return;
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      matches.forEach((g) => next.add(itemKey(g)));
      return next;
    });
    const first = matches[0].last4;
    const t = setTimeout(() => {
      document.querySelector(`[data-gap-last4="${first}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return () => clearTimeout(t);
  }, [focusParam, allGaps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when case changes
  useEffect(() => {
    setCheckedKeys(new Set());
    setKnownKeys(new Set());
  }, [caseId]);

  const toggleKey = useCallback((key: string) => {
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleAllMissing = useCallback(() => {
    const keys = missingItems.map(itemKey);
    const allChecked = keys.every(k => checkedKeys.has(k));
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (allChecked) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }, [missingItems, checkedKeys]);

  const toggleAllTransfer = useCallback(() => {
    const keys = transferItems.map(itemKey);
    const allChecked = keys.every(k => checkedKeys.has(k));
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (allChecked) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }, [transferItems, checkedKeys]);

  const toggleAllTrailing = useCallback(() => {
    const keys = trailingItems.map(itemKey);
    const allChecked = keys.every(k => checkedKeys.has(k));
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (allChecked) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }, [trailingItems, checkedKeys]);

  const toggleAllLeading = useCallback(() => {
    const keys = leadingItems.map(itemKey);
    const allChecked = keys.every(k => checkedKeys.has(k));
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (allChecked) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }, [leadingItems, checkedKeys]);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Letter settings local state
  const DEFAULT_SETTINGS: DemandLetterSettings = {
    notice_days: 10,
    delivery_method: 'Via Electronic Mail / Certified Mail',
    intro_override: null,
  };
  const [localSettings, setLocalSettings] = useState<DemandLetterSettings>(DEFAULT_SETTINGS);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (savedSettings) {
      setLocalSettings({
        notice_days: savedSettings.notice_days ?? 10,
        delivery_method: savedSettings.delivery_method ?? 'Via Electronic Mail / Certified Mail',
        intro_override: savedSettings.intro_override ?? null,
      });
    }
  }, [savedSettings]);

  const handleSettingsBlur = useCallback(() => {
    updateSettings.mutate(localSettings);
  }, [localSettings, updateSettings]);

  const firmConfigured = !!(firmSettings?.attorney_name || firmSettings?.firm_name);
  const selectedCount = allGaps.filter(g => checkedKeys.has(itemKey(g))).length;
  const hasAnyItems = allGaps.length > 0;

  // Remedy routing groups (server-computed request_from on every gap)
  const routedOpposing = allGaps.filter(g => g.request_from === 'opposing');
  const routedClient = allGaps.filter(g => g.request_from === 'client');
  const routedSubpoena = allGaps.filter(g => g.request_from === 'subpoena');
  const routedUnresolved = allGaps.filter(g => !g.request_from || g.request_from === 'resolve_ownership_first');
  const selectedOpposingCount = routedOpposing.filter(g => checkedKeys.has(itemKey(g))).length;
  const selectedNonLetterCount = selectedCount - selectedOpposingCount;

  const clientChecklistText = (() => {
    if (routedClient.length === 0) return '';
    const lines: string[] = [`Documents to request from ${caseData?.client_name || 'the client'}:`, ''];
    routedClient.forEach((g, i) => {
      const label = g.institution ? `${g.institution} ${g.account_type}${g.last4 ? ` …${g.last4}` : ''}` : g.account_label;
      if (g.gap_type === 'missing_months') {
        lines.push(`${i + 1}. ${label} — statements for: ${groupConsecutiveMonths(g.missing_months).join(', ')}`);
      } else if (g.gap_type === 'incomplete_copy') {
        lines.push(`${i + 1}. ${label} — complete copy of the ${g.coverage_start ? formatDate(g.coverage_start) : '?'} statement (only ${g.pages_produced} of ${g.pages_declared} pages on file)`);
      } else if (g.gap_type === 'balance_discontinuity') {
        const moved = g.balance_delta != null && Math.abs(g.balance_delta) > 0.01
          ? ` (their statements' own balances show ${formatCurrency(Math.abs(g.balance_delta))} moved in this window)`
          : '';
        lines.push(`${i + 1}. ${label} — statements covering ${g.coverage_start ? formatDate(g.coverage_start) : '?'} through ${g.coverage_end ? formatDate(g.coverage_end) : '?'}${moved}`);
      } else if (g.gap_type === 'trailing_gap') {
        const from = g.trailing_months?.[0];
        lines.push(`${i + 1}. ${label} — statements from ${from ? formatMonth(from) : 'the last statement on file'} through the present`);
      } else if (g.gap_type === 'leading_gap') {
        lines.push(`${i + 1}. ${label} — statements for: ${groupConsecutiveMonths(g.leading_months ?? []).join(', ')} (before the earliest statement produced)`);
      } else {
        const amt = g.total_transferred != null ? ` (received ${formatCurrency(Math.abs(g.total_transferred))} across ${g.transfer_count} transfer${(g.transfer_count ?? 0) !== 1 ? 's' : ''} from their accounts)` : '';
        lines.push(`${i + 1}. Identify account ${g.account_label}${amt} and provide its statements`);
      }
    });
    return lines.join('\n');
  })();
  const totalMissingMonths = missingItems.reduce((s, g) => s + g.missing_months.length, 0);
  const totalLeadingMonths = leadingItems.reduce((s, g) => s + (g.leading_months?.length ?? 0), 0);
  const totalTrailingMonths = trailingItems.reduce((s, g) => s + (g.months_stale ?? (g.trailing_months?.length ?? 0)), 0);
  const totalTransferAmount = formatCurrency(Math.abs(transferItems.reduce((s, g) => s + (g.total_transferred ?? 0), 0)));

  const allMissingChecked = missingItems.length > 0 && missingItems.every(g => checkedKeys.has(itemKey(g)));
  const allLeadingChecked = leadingItems.length > 0 && leadingItems.every(g => checkedKeys.has(itemKey(g)));
  const allTransferChecked = transferItems.length > 0 && transferItems.every(g => checkedKeys.has(itemKey(g)));
  const allTrailingChecked = trailingItems.length > 0 && trailingItems.every(g => checkedKeys.has(itemKey(g)));
  const allDiscontinuityChecked = discontinuityItems.length > 0 && discontinuityItems.every(g => checkedKeys.has(itemKey(g)));
  const totalDiscontinuityMoved = discontinuityItems.reduce(
    (s, g) => s + Math.abs(g.balance_delta ?? 0), 0);

  const allIncompleteChecked = incompleteItems.length > 0 && incompleteItems.every(g => checkedKeys.has(itemKey(g)));
  const totalIncompleteMoved = incompleteItems.reduce((s, g) => s + Math.abs(g.balance_delta ?? 0), 0);

  const toggleAllIncomplete = useCallback(() => {
    const keys = incompleteItems.map(itemKey);
    const allChecked = keys.every(k => checkedKeys.has(k));
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (allChecked) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
  }, [incompleteItems, checkedKeys]);

  const toggleAllDiscontinuity = useCallback(() => {
    const keys = discontinuityItems.map(itemKey);
    const allChecked = keys.every(k => checkedKeys.has(k));
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (allChecked) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }, [discontinuityItems, checkedKeys]);

  const handleGenerate = async () => {
    const selectedItems = allGaps
      .filter(g => checkedKeys.has(itemKey(g)))
      .map(g => ({ account_label: g.account_label, gap_type: g.gap_type }));
    try {
      await generateMutation.mutateAsync({
        selected_items: selectedItems,
        notice_days: localSettings.notice_days,
        delivery_method: localSettings.delivery_method,
        intro_override: localSettings.intro_override,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const previewText = showPreview
    ? buildPreviewText(allGaps, checkedKeys, firmSettings, caseData, localSettings)
    : '';

  return (
    <>
      {/* data-print="hide": Cmd-P prints only the letter (from the preview modal). */}
      <div data-print="hide" className="px-6 sm:px-8 pt-8 pb-20 max-w-4xl mx-auto space-y-8">

        {/* Letterhead / firm identity warning — a letter without the firm's
            letterhead is not sendable; say so loudly, not in faint gray. */}
        {(!firmSettings?.has_template || !firmConfigured) && (
          <div className="flex items-start gap-3 px-4 py-3 bg-clay-50 border border-clay-300 rounded-lg text-sm text-clay-900">
            <AlertTriangle size={16} className="shrink-0 mt-px text-clay-700" />
            <div>
              <span className="font-semibold">
                {!firmConfigured && !firmSettings?.has_template
                  ? 'No firm details or letterhead on file.'
                  : !firmSettings?.has_template
                    ? 'No firm letterhead on file.'
                    : 'Firm details are incomplete.'}
              </span>{' '}
              Letters will generate with plain formatting — no logo, no firm header.
              Upload your letterhead (PDF or Word) and fill in the firm details in{' '}
              <Link to="/firm-settings" className="underline underline-offset-2 font-medium text-clay-900 hover:text-clay-700">
                Firm Settings
              </Link>{' '}
              before sending anything to opposing counsel.
            </div>
          </div>
        )}

        {/* Firm / letterhead status line */}
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-ink-soft min-w-0">
            {firmConfigured ? (
              <>
                Letters will be signed by{' '}
                <span className="font-medium text-ink">
                  {firmSettings?.attorney_name || firmSettings?.firm_name}
                </span>
                {firmSettings?.firm_name && firmSettings?.attorney_name && (
                  <> &mdash; {firmSettings.firm_name}</>
                )}
                {firmSettings?.has_template && (
                  <span className="ml-1 text-green-700">(letterhead loaded)</span>
                )}
              </>
            ) : (
              <span className="text-ink-faint">No firm information configured — letters will use plain formatting.</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="md"
              onClick={() => refetchGaps()}
              disabled={isFetching}
              title="Re-scan this case for statement gaps, trailing gaps, and transfer destinations"
            >
              <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
              Refresh findings
            </Button>
            <Link
              to="/firm-settings"
              className={cn(BUTTON_BASE, BUTTON_VARIANTS.secondary, 'h-8 gap-2 px-3 text-sm')}
            >
              <Settings size={16} />
              Firm Settings
            </Link>
          </div>
        </div>

        {/* Sticky action bar — always reachable while scrolling (floats, so it keeps a shadow) */}
        <div className="sticky top-0 z-(--z-sticky) rounded-lg border border-border bg-cream/95 backdrop-blur-sm shadow-sm px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm text-ink-soft">
              <span className="font-semibold text-ink">{selectedOpposingCount}</span>{' '}
              item{selectedOpposingCount !== 1 ? 's' : ''} in the letter
              {selectedNonLetterCount > 0 && (
                <span className="text-ink-faint"> — {selectedNonLetterCount} selected item{selectedNonLetterCount !== 1 ? 's' : ''} routed elsewhere (client / subpoena) won't be included</span>
              )}
              {!hasAnyItems && !gapsLoading && (
                <span className="text-ink-faint"> — nothing to demand</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="secondary"
                size="md"
                icon={showPreview ? EyeOff : Eye}
                onClick={() => setShowPreview(p => !p)}
                disabled={!hasAnyItems}
              >
                {showPreview ? 'Hide preview' : 'Preview'}
              </Button>
              <Button
                variant="primary"
                size="lg"
                icon={FileText}
                onClick={handleGenerate}
                loading={generateMutation.isPending}
                disabled={selectedOpposingCount === 0}
                title={selectedOpposingCount === 0 ? 'No selected items are demands for opposing counsel — client and third-party items are handled by the checklist and subpoena list below' : undefined}
              >
                {generateMutation.isPending ? 'Generating…' : 'Generate Letter'}
              </Button>
            </div>
          </div>

          {generateMutation.isError && (
            <div className="mt-2 flex items-center gap-2 text-danger text-xs bg-danger-soft border border-danger-accent rounded-md px-3 py-2">
              <AlertTriangle size={12} className="shrink-0" />
              {extractAxiosDetail(generateMutation.error)}
            </div>
          )}
          {generateMutation.isSuccess && generateMutation.data && (
            <div className="mt-2 flex items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              <div className="flex items-center gap-2 text-green-700 text-xs">
                <CheckCircle size={12} className="shrink-0" />
                Letter generated and ready.
              </div>
              <Button
                variant="primary"
                size="md"
                icon={Download}
                onClick={() => window.open(`/api/demand-letters/${generateMutation.data.id}/download`, '_blank')}
              >
                Download Now
              </Button>
            </div>
          )}
        </div>

        {/* Remedy routing summary — where each missing document must come from */}
        {hasAnyItems && (
          <Card padding="compact" className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="font-semibold text-ink">Where each item comes from:</span>
              <Tag tone="opposing">{routedOpposing.length} demand from opposing counsel</Tag>
              <Tag tone="client">{routedClient.length} ask {caseData?.client_name || 'your client'}</Tag>
              <Tag tone="third">{routedSubpoena.length} subpoena candidate{routedSubpoena.length !== 1 ? 's' : ''}</Tag>
              {routedUnresolved.length > 0 && (
                <Tag tone="unknown">{routedUnresolved.length} need{routedUnresolved.length === 1 ? 's' : ''} owner confirmed</Tag>
              )}
            </div>
            <p className="text-xs text-ink-faint">
              The letter only demands documents solely in the opposing party's control. Your client can
              supply their own and joint accounts directly, and third-party records come by subpoena.
              {routedUnresolved.length > 0 && (
                <> Unconfirmed accounts are set on the Accounts page — routing is never guessed.</>
              )}
            </p>
            {routedClient.length > 0 && (
              <div className="flex items-center gap-3 pt-1">
                <CopyButton text={clientChecklistText} label={`Copy client checklist (${routedClient.length})`} />
                <span className="text-xs text-ink-faint">Plain-language list to send to {caseData?.client_name || 'your client'}.</span>
              </div>
            )}
            {routedSubpoena.length > 0 && (
              <div className="flex items-center gap-3 pt-1 flex-wrap">
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => recordsRequestMutation.mutate()}
                  disabled={recordsRequestMutation.isPending}
                  className="bg-party-third-soft text-party-third border-transparent hover:bg-party-third-soft hover:brightness-95"
                >
                  {recordsRequestMutation.isPending
                    ? 'Generating…'
                    : `Generate records requests (${routedSubpoena.length})`}
                </Button>
                <span className="text-xs text-ink-faint">
                  Draft per-institution records requests with verified service addresses (Tex. Fin. Code §59.008) — review before serving.
                </span>
                {recordsRequestMutation.isSuccess && recordsRequestMutation.data?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`/api/demand-letters/${recordsRequestMutation.data.id}/download`, '_blank')}
                  >
                    Download draft
                  </Button>
                )}
                {recordsRequestMutation.isError && (
                  <span className="text-xs text-danger">Generation failed — try again.</span>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Live preview panel — toggled from the action bar */}
        {showPreview && hasAnyItems && (
          <Card padding="default">
            <div className="text-xs text-ink-faint mb-3 flex items-center gap-1">
              <Info size={12} className="shrink-0" />
              Read-only preview — updates as you toggle items. The actual .docx will include full formatting.
            </div>
            <pre className="text-xs text-ink-soft font-mono whitespace-pre-wrap leading-relaxed">
              {previewText}
            </pre>
          </Card>
        )}

        {/* Loading */}
        {gapsLoading && (
          <Card padding="default" className="space-y-3" role="status" aria-label="Analyzing statement coverage">
            <Skeleton width="40%" />
            <Skeleton />
            <Skeleton />
            <Skeleton width="70%" />
          </Card>
        )}

        {/* Empty state */}
        {!gapsLoading && !hasAnyItems && (
          <Card padding="none">
            <EmptyState
              icon={CheckCircle}
              title="No issues found"
              description="No statement gaps, balance discontinuities, unaccounted transfer destinations, or trailing production gaps were detected for this case."
              action={
                <Button variant="secondary" size="md" onClick={() => refetchGaps()} disabled={isFetching}>
                  <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
                  Refresh
                </Button>
              }
            />
          </Card>
        )}

        {/* Unified issue list — group → item detail (two levels) */}
        {!gapsLoading && hasAnyItems && (
          <div className="space-y-10">
            {missingItems.length > 0 && (
              <section className="space-y-3">
                <GroupHeader
                  icon={<FileWarning size={16} />}
                  title="Accounts with statement gaps"
                  subtitle={`${totalMissingMonths} missing month${totalMissingMonths !== 1 ? 's' : ''} within date ranges already produced`}
                  count={missingItems.length}
                  showSelectAll={missingItems.length > 1}
                  allChecked={allMissingChecked}
                  onToggleAll={toggleAllMissing}
                />
                <div className="space-y-1">
                  {missingItems.map((gap) => (
                    <GapCard
                      key={itemKey(gap)}
                      gap={gap}
                      checked={checkedKeys.has(itemKey(gap))}
                      onToggle={toggleKey}
                      highlight={gapMatchesFocus(gap)}
                    />
                  ))}
                </div>
              </section>
            )}

            {leadingItems.length > 0 && (
              <section className="space-y-3">
                <GroupHeader
                  icon={<History size={16} />}
                  title="Not produced back to the discovery date"
                  subtitle={`${totalLeadingMonths} month${totalLeadingMonths !== 1 ? 's' : ''} between the date discovery was requested from and the earliest statement produced`}
                  count={leadingItems.length}
                  showSelectAll={leadingItems.length > 1}
                  allChecked={allLeadingChecked}
                  onToggleAll={toggleAllLeading}
                />
                <div className="space-y-1">
                  {leadingItems.map((gap) => (
                    <LeadingGapCard
                      key={itemKey(gap)}
                      gap={gap}
                      checked={checkedKeys.has(itemKey(gap))}
                      onToggle={toggleKey}
                      caseId={caseId}
                    />
                  ))}
                </div>
              </section>
            )}

            {discontinuityItems.length > 0 && (
              <section className="space-y-3">
                <GroupHeader
                  icon={<Scale size={16} />}
                  title="Unproduced periods proven by account balances"
                  subtitle={
                    totalDiscontinuityMoved > 0.01
                      ? `${formatCurrency(totalDiscontinuityMoved)} of movement the produced statements' own balances can't account for`
                      : 'The produced statements’ printed balances establish these holes'
                  }
                  count={discontinuityItems.length}
                  showSelectAll={discontinuityItems.length > 1}
                  allChecked={allDiscontinuityChecked}
                  onToggleAll={toggleAllDiscontinuity}
                />
                <div className="space-y-1">
                  {discontinuityItems.map((gap) => (
                    <DiscontinuityCard
                      key={`${itemKey(gap)}::${gap.coverage_start ?? ''}`}
                      gap={gap}
                      checked={checkedKeys.has(itemKey(gap))}
                      onToggle={toggleKey}
                    />
                  ))}
                </div>
              </section>
            )}

            {incompleteItems.length > 0 && (
              <section className="space-y-3">
                <GroupHeader
                  icon={<FileMinus size={16} />}
                  title="Incomplete statement copies"
                  subtitle={
                    totalIncompleteMoved > 0.01
                      ? `${incompleteItems.length} statements shorted pages per their own pagination — ${formatCurrency(totalIncompleteMoved)} of activity on the missing pages`
                      : `${incompleteItems.length} statements produced with pages omitted, per their own pagination`
                  }
                  count={incompleteItems.length}
                  showSelectAll={incompleteItems.length > 1}
                  allChecked={allIncompleteChecked}
                  onToggleAll={toggleAllIncomplete}
                />
                <div className="space-y-1">
                  {incompleteItems.map((gap) => (
                    <IncompleteCopyCard
                      key={`${itemKey(gap)}::${gap.coverage_start ?? ''}`}
                      gap={gap}
                      checked={checkedKeys.has(itemKey(gap))}
                      onToggle={toggleKey}
                    />
                  ))}
                </div>
              </section>
            )}

            {trailingItems.length > 0 && (
              <section className="space-y-3">
                <GroupHeader
                  icon={<Clock size={16} />}
                  title="Not produced through present date"
                  subtitle={`${totalTrailingMonths} month${totalTrailingMonths !== 1 ? 's' : ''} outstanding since the last statement on file`}
                  count={trailingItems.length}
                  showSelectAll={trailingItems.length > 1}
                  allChecked={allTrailingChecked}
                  onToggleAll={toggleAllTrailing}
                />
                <div className="space-y-1">
                  {trailingItems.map((gap) => (
                    <TrailingGapCard
                      key={itemKey(gap)}
                      gap={gap}
                      checked={checkedKeys.has(itemKey(gap))}
                      onToggle={toggleKey}
                      caseId={caseId}
                    />
                  ))}
                </div>
              </section>
            )}

            {transferItems.length > 0 && (
              <section className="space-y-3">
                <GroupHeader
                  icon={<Landmark size={16} />}
                  title="Accounts with transfers but no statements"
                  subtitle={`${totalTransferAmount} transferred to ${transferItems.length} account${transferItems.length !== 1 ? 's' : ''} with no statements on file`}
                  count={transferItems.length}
                  showSelectAll={transferItems.length > 1}
                  allChecked={allTransferChecked}
                  onToggleAll={toggleAllTransfer}
                  extra={<CopyButton text={buildAllTransfersText(transferItems)} label="Copy All" />}
                />
                <div className="space-y-1">
                  {transferItems.map((gap) => (
                    <TransferCard
                      key={itemKey(gap)}
                      gap={gap}
                      checked={checkedKeys.has(itemKey(gap))}
                      onToggle={toggleKey}
                      onViewSource={setViewer}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Letter history */}
        {letters.length > 0 && (
          <section>
            <h2 className="t-card text-ink mb-3">Generated Letters</h2>
            <Card padding="none" className="overflow-hidden">
              <div className="divide-y divide-border-subtle">
                {letters.map(l => (
                  <LetterRow
                    key={l.id}
                    letter={l}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    isDeleting={deleteMutation.isPending && deleteMutation.variables === l.id}
                    onPreview={(id) => setPreviewLetterId(id)}
                  />
                ))}
              </div>
            </Card>
          </section>
        )}

        {/* Letter Settings accordion */}
        <section>
          <Button
            variant="ghost"
            size="md"
            icon={SlidersHorizontal}
            iconTrailing={settingsOpen ? ChevronDown : ChevronRight}
            onClick={() => setSettingsOpen(p => !p)}
          >
            Letter options
          </Button>

          {settingsOpen && (
            <Card padding="default" className="mt-3 space-y-4">
              <p className="text-xs text-ink-faint">
                These settings apply to generated letters for this case. Changes are saved automatically when you leave a field.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Notice Period (days)"
                  type="number"
                  min={1}
                  max={90}
                  value={localSettings.notice_days}
                  onChange={e => setLocalSettings(s => ({ ...s, notice_days: parseInt(e.target.value) || 10 }))}
                  onBlur={handleSettingsBlur}
                />

                <Input
                  label="Delivery Method Line"
                  type="text"
                  value={localSettings.delivery_method}
                  onChange={e => setLocalSettings(s => ({ ...s, delivery_method: e.target.value }))}
                  onBlur={handleSettingsBlur}
                  placeholder="Via Electronic Mail / Certified Mail"
                />
              </div>

              <TextArea
                label="Custom Opening Paragraph"
                hint="Optional — leave blank to use the default."
                rows={5}
                value={localSettings.intro_override ?? ''}
                onChange={e => setLocalSettings(s => ({ ...s, intro_override: e.target.value || null }))}
                onBlur={handleSettingsBlur}
                placeholder="Leave blank to use the standard opening paragraph…"
              />

              {updateSettings.isSuccess && (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle size={12} /> Settings saved.
                </p>
              )}

              {/* Example Demand Letter (style reference) — same data as Firm Settings */}
              <div className="border-t border-border-subtle pt-4">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h3 className="t-label text-ink-soft">Example Demand Letter</h3>
                  <span className="text-xs text-ink-faint">Firm-wide setting</span>
                </div>
                <p className="text-xs text-ink-faint mb-3">
                  Upload a Word (.docx) written in your voice and style. New demand letters will be
                  drafted in the same tone and structure, and reuse your firm's logo, font, and
                  margins straight from the file. A PDF guides only the wording. Shared with Firm
                  Settings.
                </p>
                {firmSettings?.has_example_letter ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 flex-1 min-w-0">
                      <FileText size={16} className="shrink-0" />
                      <span className="truncate">{firmSettings.example_letter_filename || 'Example letter uploaded'}</span>
                    </div>
                    <IconButton
                      icon={Trash2}
                      aria-label="Remove example letter"
                      variant="danger-ghost"
                      size="md"
                      onClick={() => setConfirmClearExample(true)}
                    />
                    <Button variant="secondary" size="lg" onClick={() => exampleInputRef.current?.click()}>
                      Replace
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="lg"
                    icon={Upload}
                    onClick={() => exampleInputRef.current?.click()}
                    loading={uploadExampleMutation.isPending}
                    className="border-dashed border-border-strong text-ink-soft hover:border-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    {uploadExampleMutation.isPending ? 'Uploading…' : 'Upload Example Letter (.docx or .pdf)'}
                  </Button>
                )}
                {/* eslint-disable-next-line no-restricted-syntax -- hidden native file input: no ui primitive for programmatic pickers */}
                <input
                  ref={exampleInputRef}
                  type="file"
                  accept=".docx,.pdf"
                  className="hidden"
                  onChange={handleExampleLetterUpload}
                />
                {uploadExampleMutation.isError && (
                  <p className="text-danger text-xs mt-2">
                    {(() => {
                      const detail = extractAxiosDetail(uploadExampleMutation.error);
                      const generic = 'Upload failed. Please ensure the file is a valid .docx or .pdf under 5 MB.';
                      return detail && detail !== 'Letter generation failed. Please try again.' ? detail : generic;
                    })()}
                  </p>
                )}
                {uploadExampleMutation.isSuccess && (
                  <p className="text-green-700 text-xs mt-2 flex items-center gap-1">
                    <CheckCircle size={12} /> Example letter saved — new demand letters will match your style.
                  </p>
                )}
              </div>
            </Card>
          )}
        </section>
      </div>

      {/* Source page viewer */}
      {viewer && (
        <SourcePageViewer
          statementId={viewer.statementId}
          pageNumber={viewer.pageNumber}
          sourceFile={viewer.sourceFile}
          isOpen={true}
          onClose={() => setViewer(null)}
        />
      )}

      {previewLetterId != null && (
        <LetterPreviewModal
          letterId={previewLetterId}
          onClose={() => setPreviewLetterId(null)}
        />
      )}

      <ConfirmModal
        open={confirmClearExample}
        onClose={() => setConfirmClearExample(false)}
        onConfirm={handleClearExampleLetter}
        title="Remove example letter"
        message="New demand letters will no longer follow this example's tone, structure, or formatting."
        confirmLabel="Remove"
        variant="danger"
      />
    </>
  );
}
